import React, { useState, useRef, useCallback, useEffect } from 'react'
import { ReactComponent as StopIcon } from 'assets/svgs/stop-icon.svg'
import { ReactComponent as PlayIcon } from 'assets/svgs/play-icon.svg'
import { ReactComponent as PauseIcon } from 'assets/svgs/pause-icon.svg'
// import { ReactComponent as SendIcon } from 'assets/svgs/send-icon.svg'
import { ReactComponent as DeleteIcon } from 'assets/svgs/trash.svg'

interface VoiceRecorderProps {
  onVoiceMessage: (audioBlob: Blob, transcript?: string, durationSeconds?: number) => void
  onCancel?: () => void
  className?: string
  autoStart?: boolean
}

interface RecordingState {
  isRecording: boolean
  isPlaying: boolean
  isPaused: boolean
  duration: number
  audioBlob: Blob | null
  audioUrl: string | null
  transcript: string | null
  isTranscribing: boolean
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onVoiceMessage,
  onCancel,
  className = '',
  autoStart = false,
}) => {
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    isPlaying: false,
    isPaused: false,
    duration: 0,
    audioBlob: null,
    audioUrl: null,
    transcript: null,
    isTranscribing: false,
  })

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const recognitionRef = useRef<any>(null)
  const currentTranscriptRef = useRef<string>('')
  const durationRef = useRef<number>(0)
  // Set when the user cancels, so the pending onstop discards the clip instead of attaching it
  const cancelledRef = useRef<boolean>(false)

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      const recognition = new SpeechRecognition()

      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'

      recognition.onresult = (event: any) => {
        let transcript = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript
        }

        const trimmedTranscript = transcript.trim()
        currentTranscriptRef.current = trimmedTranscript

        setState((prev) => ({
          ...prev,
          transcript: trimmedTranscript,
        }))
      }

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error)
        setState((prev) => ({ ...prev, isTranscribing: false }))
      }

      recognition.onend = () => {
        setState((prev) => ({ ...prev, isTranscribing: false }))
      }

      recognitionRef.current = recognition
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [])

  // Start recording
  const startRecording = useCallback(async () => {
    cancelledRef.current = false
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      })

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      })

      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        // Cancelled: release the mic and discard the clip (never upload/attach it)
        if (cancelledRef.current) {
          stream.getTracks().forEach((track) => track.stop())
          chunksRef.current = []
          return
        }

        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' })
        const audioUrl = URL.createObjectURL(audioBlob)

        setState((prev) => ({
          ...prev,
          audioBlob,
          audioUrl,
          isRecording: false,
        }))

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop())

        // Automatically add voice message to the input
        const currentDuration = durationRef.current
        setTimeout(() => {
          if (audioBlob && !cancelledRef.current) {
            onVoiceMessage(audioBlob, currentTranscriptRef.current || undefined, currentDuration)

            // Reset the recorder state and hide it
            setState({
              isRecording: false,
              isPlaying: false,
              isPaused: false,
              duration: 0,
              audioBlob: null,
              audioUrl: null,
              transcript: null,
              isTranscribing: false,
            })
            durationRef.current = 0

            // Clean up the audio URL
            URL.revokeObjectURL(audioUrl)
            currentTranscriptRef.current = ''
          }
        }, 500) // Longer delay to ensure everything is processed
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(100) // Collect data every 100ms

      // Start speech recognition
      if (recognitionRef.current) {
        setState((prev) => ({ ...prev, isTranscribing: true, transcript: '' }))
        recognitionRef.current.start()
      }

      // Start timer
      durationRef.current = 0
      timerRef.current = setInterval(() => {
        setState((prev) => ({
          ...prev,
          duration: prev.duration + 1,
        }))
        durationRef.current = durationRef.current + 1
      }, 1000)

      setState((prev) => ({
        ...prev,
        isRecording: true,
        duration: 0,
      }))
    } catch (error) {
      console.error('Error starting recording:', error)
      alert('Could not access microphone. Please check permissions.')
    }
  }, [onVoiceMessage])

  // Track if auto-start has been executed to prevent duplicates
  const autoStartExecutedRef = useRef(false)

  // Auto-start recording if requested (only once per mount)
  useEffect(() => {
    if (autoStart && !autoStartExecutedRef.current && !state.isRecording && !state.audioBlob) {
      autoStartExecutedRef.current = true
      startRecording()
    }
  }, [autoStart, state.isRecording, state.audioBlob, startRecording])

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop()

      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }

      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [state.isRecording])

  // Play/pause audio
  const togglePlayback = useCallback(() => {
    if (!audioRef.current || !state.audioUrl) return

    if (state.isPlaying) {
      audioRef.current.pause()
      setState((prev) => ({ ...prev, isPlaying: false, isPaused: true }))
    } else {
      audioRef.current.play()
      setState((prev) => ({ ...prev, isPlaying: true, isPaused: false }))
    }
  }, [state.isPlaying, state.audioUrl])

  const cancelRecording = useCallback(() => {
    cancelledRef.current = true

    if (state.isRecording) {
      stopRecording()
    }

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }

    if (state.audioUrl) {
      URL.revokeObjectURL(state.audioUrl)
    }

    setState({
      isRecording: false,
      isPlaying: false,
      isPaused: false,
      duration: 0,
      audioBlob: null,
      audioUrl: null,
      transcript: null,
      isTranscribing: false,
    })

    onCancel?.()
  }, [state.isRecording, state.audioUrl, onCancel, stopRecording])

  // Format duration
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  // Handle audio events
  useEffect(() => {
    if (audioRef.current && state.audioUrl) {
      const audio = audioRef.current

      const handleEnded = () => {
        setState((prev) => ({ ...prev, isPlaying: false, isPaused: false }))
      }

      audio.addEventListener('ended', handleEnded)
      return () => audio.removeEventListener('ended', handleEnded)
    }
    return undefined
  }, [state.audioUrl])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl)
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [state.audioUrl])

  // Only render when recording or has audio
  if (!state.isRecording && !state.audioBlob) {
    return null
  }

  return (
    <div className={`voice-recorder ${className}`}>
      {/* Hidden audio element for playback */}
      {state.audioUrl && <audio ref={audioRef} src={state.audioUrl} />}

      {/* Recording state */}
      {state.isRecording && (
        <div className="flex items-center gap-2 p-2 bg-white border border-dashed border-app-primary-40 rounded-lg min-w-[300px]">
          {/* Delete button */}
          <button
            onClick={cancelRecording}
            className="p-1 hover:bg-app-grey-10 rounded transition-colors flex-shrink-0"
            title="Cancel recording"
          >
            <DeleteIcon className="w-4 h-4 text-app-grey-60" />
          </button>

          {/* Recording indicator */}
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>

          {/* Waveform visualization */}
          <div className="flex items-center gap-[1px] flex-1 px-2">
            {[...Array(40)].map((_, i) => (
              <div
                key={i}
                className="w-[2px] bg-app-primary-60 rounded-full animate-pulse"
                style={{
                  height: `${Math.random() * 16 + 4}px`,
                  animationDelay: `${i * 50}ms`,
                }}
              />
            ))}
          </div>

          {/* Time display */}
          <span className="text-sm text-app-grey-70 font-mono flex-shrink-0 min-w-[40px]">
            {formatDuration(state.duration)}
          </span>

          {/* Stop/Pause button */}
          <button
            onClick={stopRecording}
            className="p-1 bg-app-primary-60 hover:bg-app-primary-70 text-white rounded transition-colors flex-shrink-0"
            title="Stop recording"
          >
            <StopIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Playback state */}
      {!state.isRecording && state.audioBlob && (
        <div className="flex items-center gap-2 p-2 bg-app-grey-5 rounded-lg min-w-[300px]">
          {/* Play/Pause button */}
          <button
            onClick={togglePlayback}
            className="p-1 bg-app-primary-60 hover:bg-app-primary-70 text-white rounded-full transition-colors flex-shrink-0"
            title={state.isPlaying ? 'Pause' : 'Play'}
          >
            {state.isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
          </button>

          {/* Time display */}
          <span className="text-sm text-app-grey-70 font-mono flex-shrink-0 min-w-[40px]">
            {formatDuration(state.duration)}
          </span>

          {/* Static waveform for playback */}
          <div className="flex items-center gap-[1px] flex-1 px-2">
            {[...Array(40)].map((_, i) => (
              <div
                key={i}
                className="w-[2px] bg-app-grey-40 rounded-full"
                style={{
                  height: `${Math.random() * 16 + 4}px`,
                }}
              />
            ))}
          </div>

          {/* Delete button */}
          <button
            onClick={cancelRecording}
            className="p-1 hover:bg-app-grey-10 rounded transition-colors flex-shrink-0"
            title="Delete recording"
          >
            <DeleteIcon className="w-4 h-4 text-app-grey-60" />
          </button>
        </div>
      )}
    </div>
  )
}
